import { useEffect, useState } from "react";
import {
  Users, MapPin, CalendarDays, Ticket, MessageSquare, DollarSign, TrendingUp, TrendingDown, Minus, Star, Crown,
  Eye, UserCheck,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import { api } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const MONTHS_SHORT_FR = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const MONTHS_SHORT_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const PIE_COLORS = ["#2563EB", "#9333EA", "#16A34A", "#F97316", "#EF4444", "#14B8A6", "#F59E0B", "#0EA5E9"];

const fmtDay = (iso, lang = "fr") => {
  const d = new Date(`${iso}T12:00:00`);
  const months = lang === "en" ? MONTHS_SHORT_EN : MONTHS_SHORT_FR;
  return lang === "en" ? `${months[d.getMonth()]} ${d.getDate()}` : `${d.getDate()} ${months[d.getMonth()]}`;
};

// Textes d'activité générés en français par le backend — reformulés côté client en EN
const trActivity = (text, lang) => {
  if (lang !== "en" || !text) return text;
  return text
    .replace(/^(.*) s'est inscrit$/, "$1 signed up")
    .replace(/^(\d+) place\(s\) — (.*)$/, "$1 spot(s) — $2")
    .replace(/^Demande approuvée — (.*)$/, "Request approved — $1")
    .replace(/^Demande refusée — (.*)$/, "Request declined — $1");
};

// Dates relatives générées en français par le backend (rel_date)
const trRelDate = (text, lang) => {
  if (lang !== "en" || !text) return text;
  if (text === "Aujourd'hui") return "Today";
  if (text === "Hier") return "Yesterday";
  return text.replace(/^Il y a (\d+) jours$/, "$1 days ago");
};

const Delta = ({ text }) => {
  const { t, lang } = useLanguage();
  if (!text) return null;
  const up = text.startsWith("+");
  const flat = text.startsWith("Stable");
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  const color = flat ? "text-slate-400" : up ? "text-emerald-600" : "text-red-500";
  return (
    <p className={`mt-1 flex items-center gap-1 text-[11px] font-semibold ${color}`}>
      <Icon className="h-3 w-3" />
      {lang === "en" ? text.replace("mois dernier", "last month") : text}
    </p>
  );
};

const Block = ({ title, action, testId, children }) => (
  <div data-testid={testId} className="min-w-0 rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
      <h3 className="text-sm font-bold text-slate-900">{title}</h3>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

// STATISTIQUES DE VISITES DU SITE (données anonymes — voir /api/analytics/visit)
const VISIT_PERIODS = [
  { key: "today", label: "Aujourd'hui" },
  { key: "7", label: "7 jours" },
  { key: "30", label: "30 jours" },
];

const VisitStats = () => {
  const { t, lang } = useLanguage();
  const [period, setPeriod] = useState("7");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/admin/analytics/visits", { params: { period } })
      .then((r) => {
        if (!cancelled) setStats(r.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [period]);

  const cards = stats
    ? [
        { icon: Users, label: "Visiteurs aujourd'hui", value: stats.visitors_today, color: "#2563EB", bg: "#EFF6FF", tid: "visit-kpi-today" },
        { icon: UserCheck, label: "Visiteurs uniques (7 jours)", value: stats.unique_7d, color: "#9333EA", bg: "#FAF5FF", tid: "visit-kpi-7d" },
        { icon: UserCheck, label: "Visiteurs uniques (30 jours)", value: stats.unique_30d, color: "#14B8A6", bg: "#F0FDFA", tid: "visit-kpi-30d" },
        { icon: Eye, label: "Pages vues (total)", value: stats.total_pageviews, color: "#F97316", bg: "#FFF7ED", tid: "visit-kpi-pageviews" },
      ]
    : null;

  const xTick = (l) => (period === "today" ? l : fmtDay(l, lang));

  return (
    <>
      {/* 4 CARTES VISITES — même design que les KPI existants */}
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4" data-testid="admin-visit-kpis">
        {cards
          ? cards.map((c) => (
              <div key={c.tid} data-testid={c.tid} className="rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl" style={{ backgroundColor: c.bg }}>
                  <c.icon size={16} style={{ color: c.color }} />
                </div>
                <p className="mt-2.5 text-xl font-extrabold leading-none text-slate-900">{c.value}</p>
                <p className="mt-1 text-xs font-medium text-slate-500">{t(c.label)}</p>
              </div>
            ))
          : [0, 1, 2, 3].map((i) => <div key={i} className="h-[92px] animate-pulse rounded-2xl border border-slate-200 bg-white" />)}
      </div>

      {/* GRAPHIQUE « VISITES DU SITE » */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="admin-visits-chart">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900">
            <Eye className="h-4 w-4 text-[#2563EB]" />
            {t("Visites du site")}
          </h3>
          <div className="flex gap-1.5" data-testid="visits-period-filters">
            {VISIT_PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                data-testid={`visits-period-${p.key}`}
                onClick={() => setPeriod(p.key)}
                className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition ${
                  period === p.key
                    ? "border-[#2563EB] bg-[#2563EB] text-white"
                    : "border-slate-200 bg-white text-slate-500 hover:border-blue-300 hover:text-[#2563EB]"
                }`}
              >
                {t(p.label)}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-3 h-52">
          {loading && !stats ? (
            <div className="h-full animate-pulse rounded-xl bg-slate-50" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats?.series || []} margin={{ top: 5, right: 8, bottom: 0, left: -22 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="label" tickFormatter={xTick} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} interval={period === "today" ? 2 : period === "30" ? 4 : 0} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <Tooltip labelFormatter={xTick} contentStyle={{ borderRadius: 14, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="visits" name={t("Pages vues")} stroke="#2563EB" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="visitors" name={t("Visiteurs uniques")} stroke="#9333EA" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </>
  );
};

export const AdminOverview = ({ data, period, go }) => {
  const { t, lang } = useLanguage();
  if (!data) {
    return <div className="mt-5 h-96 animate-pulse rounded-2xl bg-white" />;
  }

  const k = data.kpis;
  const d = data.deltas || {};
  const days = period === "7" ? 7 : 14;
  const series = data.series.slice(-days);

  const cards = [
    { icon: Users, label: "Utilisateurs", value: k.users, delta: d.users, color: "#2563EB", bg: "#EFF6FF" },
    { icon: MapPin, label: "Établissements", value: k.establishments, delta: null, color: "#F97316", bg: "#FFF7ED" },
    { icon: CalendarDays, label: "Événements", value: k.events, delta: null, color: "#9333EA", bg: "#FAF5FF" },
    { icon: Ticket, label: "Réservations", value: k.reservations, delta: d.reservations, color: "#14B8A6", bg: "#F0FDFA" },
    { icon: MessageSquare, label: "Avis", value: k.reviews, delta: d.reviews, color: "#EC4899", bg: "#FDF2F8" },
    { icon: DollarSign, label: "Revenus", value: lang === "en" ? `$${k.revenue.toFixed(2)}` : `${k.revenue.toFixed(2).replace(".", ",")} $`, delta: d.revenue, color: "#16A34A", bg: "#F0FDF4" },
  ];

  const catTotal = data.establishments_by_category.reduce((s, c) => s + c.count, 0);
  const sub = data.subscriptions || { gratuit: 0, pro: 0, total: 0 };
  const subRows = [
    { label: "Gratuit", count: sub.gratuit, color: "#94A3B8" },
    { label: "Pro", count: sub.pro, color: "#2563EB" },
  ];

  return (
    <div className="mt-5 space-y-4" data-testid="admin-overview">
      {/* 6 KPI COMPACTS SUR UNE LIGNE */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6" data-testid="admin-kpis">
        {cards.map((c) => (
          <div key={c.label} data-testid={`admin-kpi-${c.label.toLowerCase().replace(/[^a-z]/g, "-")}`} className="rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl" style={{ backgroundColor: c.bg }}>
                <c.icon size={16} style={{ color: c.color }} />
              </div>
            </div>
            <p className="mt-2.5 text-xl font-extrabold leading-none text-slate-900">{c.value}</p>
            <p className="mt-1 text-xs font-medium text-slate-500">{t(c.label)}</p>
            <Delta text={c.delta} />
          </div>
        ))}
      </div>

      {/* STATISTIQUES DE VISITES DU SITE */}
      <VisitStats />

      {/* GRAPHIQUES : LIGNE + DONUT */}
      <div className="grid gap-4 xl:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm xl:col-span-2" data-testid="admin-activity-chart">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900">
              <TrendingUp className="h-4 w-4 text-[#2563EB]" />
              {t("Évolution de l'activité")}
            </h3>
            <span className="text-[11px] font-medium text-slate-400">{lang === "en" ? `Last ${days} days` : `${days} derniers jours`}</span>
          </div>
          <div className="mt-3 h-52">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 5, right: 8, bottom: 0, left: -22 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(iso) => fmtDay(iso, lang)} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v} $`} />
                <Tooltip
                  labelFormatter={(iso) => fmtDay(iso, lang)}
                  formatter={(value, name) => (name === "Revenus" ? [`${Number(value).toFixed(2).replace(".", ",")} $`, name] : [value, name])}
                  contentStyle={{ borderRadius: 14, border: "1px solid #E2E8F0", fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line yAxisId="left" type="monotone" dataKey="inscriptions" name={t("Utilisateurs")} stroke="#9333EA" strokeWidth={2.5} dot={false} />
                <Line yAxisId="left" type="monotone" dataKey="reservations" name={t("Réservations")} stroke="#2563EB" strokeWidth={2.5} dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="revenus" name={t("Revenus")} stroke="#2563EB" strokeWidth={2} strokeDasharray="6 4" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="admin-category-chart">
          <h3 className="text-sm font-bold text-slate-900">{t("Répartition par catégorie")}</h3>
          <div className="mt-1 flex items-center gap-2">
            <div className="relative h-36 w-36 shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.establishments_by_category}
                    dataKey="count"
                    nameKey="name"
                    innerRadius="62%"
                    outerRadius="92%"
                    paddingAngle={3}
                    strokeWidth={0}
                  >
                    {data.establishments_by_category.map((entry, i) => (
                      <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 14, border: "1px solid #E2E8F0", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-lg font-extrabold leading-none text-slate-900">{catTotal}</span>
                <span className="text-[10px] text-slate-400">{t("spots")}</span>
              </div>
            </div>
            <ul className="min-w-0 flex-1 space-y-1.5">
              {data.establishments_by_category.slice(0, 6).map((c, i) => (
                <li key={c.name} className="flex items-center gap-2 text-xs">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span className="min-w-0 flex-1 truncate font-medium text-slate-600">{t(c.name)}</span>
                  <span className="shrink-0 font-bold text-slate-900">{Math.round((c.count / Math.max(1, catTotal)) * 100)} %</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* BLOCS DU BAS */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Block
          title={t("Établissements les plus populaires")}
          testId="admin-popular"
          action={
            <button type="button" data-testid="popular-see-all" onClick={() => go("etablissements")} className="text-xs font-semibold text-[#2563EB] hover:underline">
              {t("Voir tout")}
            </button>
          }
        >
          {data.popular_establishments.length === 0 ? (
            <p className="py-4 text-center text-xs text-slate-400">{t("Aucune vue enregistrée pour le moment.")}</p>
          ) : (
            <ul className="divide-y divide-slate-50">
              {data.popular_establishments.map((e, i) => (
                <li key={e.slug} data-testid={`popular-${e.slug}`} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                  <span className="w-4 shrink-0 text-center text-xs font-extrabold text-slate-300">{i + 1}</span>
                  <img src={e.image} alt={e.name} className="h-10 w-10 shrink-0 rounded-xl object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-900">{e.name}</p>
                    <p className="truncate text-[11px] text-slate-400">{t(e.category_label)} • {e.neighborhood}</p>
                  </div>
                  <span className="flex shrink-0 items-center gap-1 text-[11px] font-medium text-amber-500">
                    <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                    {String(e.rating).replace(".", ",")}
                  </span>
                  <span className="shrink-0 rounded-full bg-[#EFF6FF] px-2 py-0.5 text-[10px] font-bold text-[#2563EB]">
                    {e.views} {t("vues")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Block>

        <Block
          title={t("Avis à modérer")}
          testId="admin-recent-reviews"
          action={
            <button type="button" data-testid="reviews-see-all" onClick={() => go("avis")} className="text-xs font-semibold text-[#2563EB] hover:underline">
              {t("Voir tout")}
            </button>
          }
        >
          {data.recent_reviews.length === 0 ? (
            <p className="py-4 text-center text-xs text-slate-400">{t("Aucun avis pour le moment.")}</p>
          ) : (
            <ul className="divide-y divide-slate-50">
              {data.recent_reviews.map((r) => (
                <li key={r.id} data-testid={`recent-review-${r.id}`} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EFF6FF] text-xs font-bold text-[#2563EB]">
                    {r.user_name?.[0]?.toUpperCase() || "A"}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                      <span className="truncate">{r.user_name}</span>
                      <span className="flex shrink-0 items-center gap-0.5 text-[11px] font-medium text-amber-500">
                        <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                        {r.rating}/5
                      </span>
                    </p>
                    <p className="truncate text-[11px] text-slate-400">{r.comment || "—"} • sur {r.item_label}</p>
                  </div>
                  <span className="shrink-0 text-[10px] text-slate-400">{trRelDate(r.date, lang)}</span>
                </li>
              ))}
            </ul>
          )}
        </Block>

        <Block title={t("Activité récente")} testId="admin-recent-activity">
          {data.recent_activity.length === 0 ? (
            <p className="py-4 text-center text-xs text-slate-400">{t("Aucune activité récente.")}</p>
          ) : (
            <ul className="divide-y divide-slate-50">
              {data.recent_activity.map((a, i) => (
                <li key={i} data-testid={`activity-${i}`} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                      a.label === "Inscription"
                        ? "bg-[#FAF5FF] text-[#9333EA]"
                        : a.label === "Réservation"
                          ? "bg-[#F0FDFA] text-[#14B8A6]"
                          : "bg-[#EFF6FF] text-[#2563EB]"
                    }`}
                  >
                    {t(a.label)}
                  </span>
                  <p className="min-w-0 flex-1 truncate text-xs text-slate-600">{trActivity(a.text, lang)}</p>
                  <span className="shrink-0 text-[10px] text-slate-400">{trRelDate(a.date, lang)}</span>
                </li>
              ))}
            </ul>
          )}
        </Block>

        <Block title={t("Abonnements")} testId="admin-subscriptions">
          <ul className="space-y-3">
            {subRows.map((s) => {
              const pct = sub.total ? Math.round((s.count / sub.total) * 100) : 0;
              return (
                <li key={t(s.label)}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 font-semibold text-slate-700">
                      {s.label === "Pro" && <Crown className="h-3.5 w-3.5 text-[#2563EB]" />}
                      {t(s.label)}
                    </span>
                    <span className="font-bold text-slate-900">
                      {s.count} <span className="font-medium text-slate-400">({pct} %)</span>
                    </span>
                  </div>
                  <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: s.color }} />
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 text-xs">
            <span className="font-medium text-slate-500">{t("Total des comptes actifs")}</span>
            <span className="text-sm font-extrabold text-slate-900">{sub.total}</span>
          </div>
        </Block>
      </div>
    </div>
  );
};

export const useAdminDashboard = () => {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  return { data, reload: load };
};
